/**
 * Pure grid geometry: time <-> pixel math and cell hit-testing.
 * No DOM access — callers supply the current column width (it flexes with the
 * viewport and lives in a ref + CSS variable, so geometry changes never force
 * React renders).
 */
import { MCGridConfig } from "./types";

export const minutesToTime = (min: number): string => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

export const dateToMinutes = (d: Date): number => d.getHours() * 60 + d.getMinutes();

/** Snap a minute value down to the slot raster and clamp into the visible day. */
export const clampSlot = (cfg: MCGridConfig, min: number): number => {
  const snapped = Math.floor(min / cfg.slotMin) * cfg.slotMin;
  return Math.min(Math.max(snapped, cfg.dayStartMin), cfg.dayEndMin - cfg.slotMin);
};

export const slotCount = (cfg: MCGridConfig): number =>
  (cfg.dayEndMin - cfg.dayStartMin) / cfg.slotMin;

export const bodyHeightPx = (cfg: MCGridConfig): number => slotCount(cfg) * cfg.slotHeightPx;

/** Y offset (px, body-relative) for a minute of day. */
export const yForMin = (cfg: MCGridConfig, min: number): number =>
  ((min - cfg.dayStartMin) / cfg.slotMin) * cfg.slotHeightPx;

/** Minute of day (slot-snapped) for a body-relative Y offset. */
export const minForY = (cfg: MCGridConfig, y: number): number =>
  clampSlot(cfg, cfg.dayStartMin + Math.floor(y / cfg.slotHeightPx) * cfg.slotMin);

/** Column index for a body-relative X offset. */
export const colForX = (x: number, colWidth: number, colCount: number): number =>
  Math.min(Math.max(Math.floor(x / colWidth), 0), colCount - 1);

/** Pixel rect (body-relative) of a time range within a column. */
export const rectForRange = (
  cfg: MCGridConfig,
  colIndex: number,
  colWidth: number,
  startMin: number,
  endMin: number,
): { left: number; top: number; width: number; height: number } => ({
  left: colIndex * colWidth,
  top: yForMin(cfg, startMin),
  width: colWidth,
  height: Math.max(yForMin(cfg, endMin) - yForMin(cfg, startMin), cfg.slotHeightPx),
});

/** Hour marks for the time gutter. */
export const hourMarks = (cfg: MCGridConfig): number[] => {
  const marks: number[] = [];
  const firstHour = Math.ceil(cfg.dayStartMin / 60);
  const lastHour = Math.floor(cfg.dayEndMin / 60);
  for (let h = firstHour; h <= lastHour; h++) marks.push(h * 60);
  return marks;
};
